import React, { useMemo, useState } from 'react';

import { useAuth } from 'src/features/auth/AuthContext';
import { useProjectMembers } from '../hooks/useProjectMembers';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

import { useRisks } from '../hooks/useRisks';
import { useCreateRisk } from '../hooks/useCreateRisk';
import { useUpdateRisk } from '../hooks/useUpdateRisk';
import { useDeleteRisk } from '../hooks/useDeleteRisk';

import RiskOverview from './RiskOverview';
import RiskListForm from './RiskListForm';

export default function RiskListContainer({ projectId }: { projectId: string }) {
    const { me } = useAuth();
    const currentUserId = me?.id;

    // 권한
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditRisk = useMemo(() => !accessLoading && hasPerm(access?.permissions, 'risk.edit'), [accessLoading, access]);

    const [includeClosed, setIncludeClosed] = useState(false);
    const [filterCategory, setFilterCategory] = useState<string | null>(null);

    const membersQ = useProjectMembers(projectId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const members = membersQ.data ?? [];

    // ✅ Task와 동일하게 Record map도 만들어줌(모달에 전달)
    const userNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        (members as any[]).forEach((mm) => {
            const uid = mm.userId;
            const name = mm.user?.name ?? '';
            const email = mm.user?.email ?? '';
            map[uid] = name && email ? `${name} <${email}>` : name || email || uid;
        });
        return map;
    }, [members]);

    const memberMap = useMemo(() => {
        const m = new Map<string, { name: string; email: string }>();
        members.forEach((mm: any) => {
            const userId = mm.userId;
            const name = mm.user?.name ?? '';
            const email = mm.user?.email ?? '';
            m.set(userId, { name, email });
        });
        return m;
    }, [members]);

    const riskQ = useRisks(projectId, { includeClosed, category: filterCategory });
    const createM = useCreateRisk(projectId);
    const updateM = useUpdateRisk(projectId);
    const deleteM = useDeleteRisk(projectId);


    return (
        <div className="w-full space-y-6">
            <RiskOverview risks={riskQ.data ?? []} onSelectCategory={setFilterCategory} />
            <RiskListForm
                risks={riskQ.data ?? []}
                projectId={projectId}
                canEdit={canEditRisk}
                includeClosed={includeClosed}
                setIncludeClosed={setIncludeClosed}
                filterCategory={filterCategory}
                setFilterCategory={setFilterCategory}
                clearFilters={() => {
                    setFilterCategory(null);
                    setIncludeClosed(true);
                }}
                reloadRisks={() => riskQ.refetch()}
                memberMap={memberMap}
                currentUserId={currentUserId}
                userNameMap={userNameMap}
                onCreate={(input) => createM.mutateAsync(input)}
                onUpdate={(riskId, patch) => updateM.mutateAsync({ riskId, patch })}
                onDelete={(riskId) => deleteM.mutateAsync(riskId)}
            />
        </div>
    );
}
